import Phaser from 'phaser';
import {
  COLORS,
  DEPTHS,
  FONTS,
  GAME_HEIGHT,
  GAME_WIDTH,
  toHexColor,
} from '../config/gameConfig';
import type { EventBus } from '../systems/EventBus';
import { propagateDepth } from './propagateDepth';

const CARD_W = 540;
const CARD_H = 360;
const PAD_X = 28;
const PAD_Y = 24;

interface DescriptionLine {
  label: string;
  body: string;
}

const LINES: readonly DescriptionLine[] = [
  { label: 'Inbox', body: 'CFO + customer emails. Click any one to read; pick how to handle it.' },
  { label: 'Slack',  body: 'Jamie + Marcus. Underlined AR terms are clickable.' },
  { label: 'AR Aging', body: 'The spreadsheet. Click any row to dunn that customer.' },
  { label: 'Payments', body: 'Wires arrive here. Click Apply on each.' },
  { label: 'Field Guide', body: 'Definitions for every term you\'ll see today.' },
];

/** First-time welcome overlay. Pauses procedural events while it's open
 *  so the player can look around without things piling up. */
export class WelcomeCard {
  private container: Phaser.GameObjects.Container | null = null;
  private escHandler: ((ev: KeyboardEvent) => void) | null = null;
  private dismissed = false;

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly bus: EventBus,
    private readonly onDismiss: (mode: 'normal' | 'slow') => void,
  ) {
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.teardown());
    scene.events.once(Phaser.Scenes.Events.DESTROY, () => this.teardown());
    this.render();
  }

  private render(): void {
    const x = Math.round((GAME_WIDTH - CARD_W) / 2);
    const y = Math.round((GAME_HEIGHT - CARD_H) / 2);

    const root = this.scene.add.container(0, 0);
    root.setDepth(DEPTHS.modal);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, COLORS.black, 0.55)
      .setOrigin(0, 0)
      .setInteractive(); // Trap clicks while welcome is up.
    root.add(backdrop);

    const panel = this.scene.add
      .rectangle(x, y, CARD_W, CARD_H, COLORS.windowBody)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive();
    root.add(panel);

    const title = this.scene.add.text(
      x + PAD_X,
      y + PAD_Y,
      'Welcome — first day on the close.',
      {
        fontFamily: FONTS.ui,
        fontSize: '20px',
        color: toHexColor(COLORS.textPrimary),
        fontStyle: 'bold',
      },
    );
    root.add(title);

    const subtitle = this.scene.add.text(
      x + PAD_X,
      y + PAD_Y + title.height + 6,
      'Take a moment to look around. Nothing new will arrive until you hit Begin.',
      {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textMuted),
        wordWrap: { width: CARD_W - PAD_X * 2 },
      },
    );
    root.add(subtitle);

    let lineY = y + PAD_Y + title.height + subtitle.height + 22;
    for (const line of LINES) {
      const labelEl = this.scene.add.text(x + PAD_X, lineY, line.label, {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.markedTermUnderline),
        fontStyle: 'bold',
      });
      root.add(labelEl);

      const bodyEl = this.scene.add.text(
        x + PAD_X + 92,
        lineY,
        line.body,
        {
          fontFamily: FONTS.ui,
          fontSize: '13px',
          color: toHexColor(COLORS.textPrimary),
          wordWrap: { width: CARD_W - PAD_X * 2 - 100 },
        },
      );
      root.add(bodyEl);

      lineY += Math.max(labelEl.height, bodyEl.height) + 6;
    }

    // Buttons at the bottom.
    const btnHeight = 38;
    const btnY = y + CARD_H - PAD_Y - btnHeight;
    const beginW = 120;
    const slowW = 160;
    const gap = 12;
    const totalW = beginW + slowW + gap;
    const beginX = x + CARD_W - PAD_X - beginW;
    const slowX = beginX - gap - slowW;

    const beginBtn = this.scene.add
      .rectangle(beginX, btnY, beginW, btnHeight, COLORS.buttonPrimary)
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    const beginLabel = this.scene.add.text(
      beginX + beginW / 2,
      btnY + btnHeight / 2,
      'Begin',
      {
        fontFamily: FONTS.ui,
        fontSize: '14px',
        color: toHexColor(COLORS.buttonPrimaryText),
        fontStyle: 'bold',
      },
    ).setOrigin(0.5, 0.5);
    beginBtn.on('pointerover', () => beginBtn.setFillStyle(COLORS.buttonHover));
    beginBtn.on('pointerout', () => beginBtn.setFillStyle(COLORS.buttonPrimary));
    beginBtn.on('pointerdown', () => this.dismiss('normal'));
    root.add(beginBtn);
    root.add(beginLabel);

    const slowBtn = this.scene.add
      .rectangle(slowX, btnY, slowW, btnHeight, COLORS.windowChrome)
      .setOrigin(0, 0)
      .setStrokeStyle(1, COLORS.windowBorder)
      .setInteractive({ useHandCursor: true });
    const slowLabel = this.scene.add.text(
      slowX + slowW / 2,
      btnY + btnHeight / 2,
      'Take it slow (0.5×)',
      {
        fontFamily: FONTS.ui,
        fontSize: '13px',
        color: toHexColor(COLORS.textPrimary),
      },
    ).setOrigin(0.5, 0.5);
    slowBtn.on('pointerover', () => slowBtn.setFillStyle(COLORS.windowTitleBar));
    slowBtn.on('pointerout', () => slowBtn.setFillStyle(COLORS.windowChrome));
    slowBtn.on('pointerdown', () => this.dismiss('slow'));
    root.add(slowBtn);
    root.add(slowLabel);

    const hint = this.scene.add.text(
      x + PAD_X,
      btnY + btnHeight / 2,
      'Adjust pace anytime via the gear icon.',
      {
        fontFamily: FONTS.ui,
        fontSize: '11px',
        color: toHexColor(COLORS.textMuted),
        fontStyle: 'italic',
      },
    ).setOrigin(0, 0.5);
    root.add(hint);

    this.escHandler = (ev: KeyboardEvent) => { if (ev.key === 'Escape') this.dismiss('normal'); };
    this.scene.input.keyboard?.on('keydown', this.escHandler);

    this.container = root;
    propagateDepth(root, DEPTHS.modal);

    // totalW is referenced for layout sanity — keep the variable around.
    void totalW;
  }

  private dismiss(mode: 'normal' | 'slow'): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.teardown();
    this.onDismiss(mode);
  }

  private teardown(): void {
    if (this.escHandler) {
      this.scene.input.keyboard?.off('keydown', this.escHandler);
      this.escHandler = null;
    }
    if (this.container) {
      this.container.destroy();
      this.container = null;
    }
  }
}
